'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { generateBlogKey } from '@eliadmin/core/src/utils/blog-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buildUrl, getSlugFromPage } from '@eliadmin/core/src/utils/url-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { RiArrowRightSLine } from 'react-icons/ri';

const [BlogCategoriesProvider, useBlogCategories] = createContextStore<
  { categories: BlogCategory[]; activeCategoryIds: number[] },
  { setActiveCategories: (categoryIds: number[]) => void }
>({ categories: [], activeCategoryIds: [] }, (set, get) => ({
  setActiveCategories: (categoryIds: number[]) => set({ activeCategoryIds: categoryIds }),
}));

export { BlogCategoriesProvider, useBlogCategories };

export type BlogCardsProps = { query: FetchBlogsQuery };

export const BlogCards = (props: BlogCardsProps) => {
  const { query: queryProp } = props;

  const locale = useLocaleStore((state) => state.currentLocale);
  const { t } = useTranslations();

  const searchParams = useSearchParams();

  const search = searchParams.get('search');
  const activeCategories = useBlogCategories((s) => s.activeCategoryIds);
  const setActiveCategories = useBlogCategories((s) => s.setActiveCategories);

  useEffect(() => {
    const filterValue = searchParams.get('filter');
    const activeCategoryIds = filterValue?.includes('categories.id IN [')
      ? filterValue.match(/\d+/g)?.map(Number) || []
      : [];

    setActiveCategories(activeCategoryIds);
  }, [searchParams, setActiveCategories]);

  const query = useMemo(
    () => ({
      order: 'desc',
      orderBy: 'created_at',
      where:
        !('filter' in queryProp) && activeCategories.length > 0
          ? { categories: { in: activeCategories } }
          : undefined,
      search: search ?? undefined,
      ...queryProp,
    }),
    [activeCategories, queryProp, search],
  );

  const {
    data: blogs,
    fetchNextPage,
    isFetching,
    hasNextPage,
  } = useSuspenseInfiniteQuery({
    initialPageParam: 0,
    queryKey: ['blogs', query],
    queryFn: ({ pageParam }) => fetchBlogs({ locale, query: { ...query, page: pageParam } as any }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
  });

  const flatList = blogs.pages.flatMap((page) => page.data) ?? [];

  return (
    <div className={cn('flex flex-col gap-4', isFetching && 'opacity-30')}>
      <div className="flex flex-col gap-6">
        {flatList.map((blog, i) => {
          if (i === 0 && !activeCategories.length && !search) return null;

          return (
            <div key={generateBlogKey(blog)}>
              <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row">
                <Link
                  href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
                  aria-label={blog.name}
                  className="relative h-[175px] w-full rounded-[10px] md:w-[220px] lg:w-[292px]"
                >
                  <Image
                    alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
                    src={blog.image?.url || '/images/placeholder.png'}
                    width={800}
                    height={480}
                    className="h-full w-full rounded-[10px]"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Link>
                <div className="flex w-full max-w-full flex-col gap-2 md:w-[510px]">
                  <div className="flex w-full flex-wrap items-center justify-between gap-2">
                    <div className="flex w-fit flex-wrap items-center justify-start gap-1 md:w-full">
                      {blog.categories?.map((category: BlogCategory, i) => {
                        if (!category.id) return null;
                        return (
                          <Link
                            key={i}
                            href={buildUrl(getSlugFromPage('blog'), {
                              search: { filter: `categories.id IN [${category.id}]` },
                            })}
                            className=" h-auto rounded-full bg-foreground px-4 py-1 text-[10px] font-semibold text-white"
                          >
                            {category.name}
                          </Link>
                        );
                      })}
                    </div>
                    <Link
                      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
                      className="whitespace-nowrap text-xs text-foreground"
                    >
                      {new Date(blog.created_at).toLocaleDateString(locale)}
                    </Link>
                  </div>

                  <Link href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}>
                    <h2
                      data-ea-visual-tag="h2"
                      className="line-clamp-1 h-7 text-lg font-bold leading-7 text-foreground"
                    >
                      {blog?.name}
                    </h2>
                  </Link>
                  <div className="relative block h-24 overflow-hidden">
                    <HtmlRenderer
                      className="ea-blog__hide-description w-full text-left text-sm leading-6 text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a]:relative [&_a]:z-10"
                      html={blog.content}
                    />
                    <Link
                      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
                      aria-label={`${t('text591')}: ${blog.name}`}
                      className="absolute inset-0 z-[1]"
                    />
                    <span className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white via-white to-transparent" />
                  </div>

                  <Link
                    href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
                    aria-label={`${t('text591')}: ${blog.name}`}
                    className="
                    flex
                    h-[36px]
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-full
                    bg-[#1eadea18]
                    text-sm
                    text-foreground
                    "
                  >
                    {t('text591')}
                    <RiArrowRightSLine size={16} />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
        {!flatList?.length && (
          <div className="flex w-full items-center justify-center">
            <p className="text-muted-foreground">{t('text554')}</p>
          </div>
        )}
        {hasNextPage && (
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetching}
            className="mx-auto w-full rounded-full bg-accent px-8 py-3 text-sm font-medium text-white shadow-none disabled:opacity-100 md:w-fit"
          >
            {t('text588')}
          </Button>
        )}
      </div>
    </div>
  );
};
